import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaPlus, FaEye, FaEdit } from "react-icons/fa";
import { RiDeleteBin3Fill } from "react-icons/ri";
import { BsThreeDots } from "react-icons/bs";
import {
  Modal,
  Button,
  Form,
  Table,
  Pagination,
  Spinner,
} from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;
const imageAPIURL = process.env.REACT_APP_Image_URL;

function DocumentList() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editDocumentName, setEditDocumentName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [documentToDeleteId, setDocumentToDeleteId] = useState(null);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({
    title: "",
    text: "",
    type: "",
  });

  const showCustomMessageModal = (title, text, type) => {
    setMessageModalContent({ title, text, type });
    setShowMessageModal(true);
  };

  const closeCustomMessageModal = () => {
    setShowMessageModal(false);
    setMessageModalContent({ title: "", text: "", type: "" });
  };

  const getAuthToken = () => {
    return localStorage.getItem("token");
  };

  const fetchDocuments = async (page = 1) => {
    setLoading(true);
    try {
      const authToken = getAuthToken();
      const headers = {
        "Content-Type": "application/json",
      };
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await fetch(
        `${API_URL}/realstate-website-document-list?page=${page}&limit=${itemsPerPage}`,
        {
          method: "GET",
          headers: headers,
        },
      );

      if (response.ok) {
        const result = await response.json();
        setDocuments(result.data || []);
        setTotalPages(result.totalPages || 1);
        setCurrentPage(page);
      } else {
        const errorData = await response.json();
        console.error("Failed to fetch documents:", errorData);
        showCustomMessageModal(
          "Error",
          `Failed to fetch documents: ${errorData.message || response.statusText}`,
          "error",
        );
      }
    } catch (error) {
      console.error("Network or unexpected error during fetch:", error);
      showCustomMessageModal(
        "Error",
        `An unexpected error occurred: ${error.message}`,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments(currentPage);
  }, [currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleViewClick = (document) => {
    setViewingDocument(document);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingDocument(null);
  };

  const handleEditClick = (document) => {
    setEditingDocument(document);
    setEditDocumentName(document.name);
    setSelectedFiles([]);
    setShowEditModal(true);
  };

  const handleUpdateDocument = async (event) => {
    event.preventDefault();
    if (!editingDocument) return;

    if (!editDocumentName.trim()) {
      showCustomMessageModal(
        "Validation Error",
        "Document name cannot be empty.",
        "warning",
      );
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("name", editDocumentName);

    selectedFiles.forEach((file) => {
      formData.append("document", file);
    });

    try {
      const authToken = getAuthToken();
      const headers = {};
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await fetch(
        `${API_URL}/realstate-website-document-update/${editingDocument.id}`,
        {
          method: "POST",
          headers: headers,
          body: formData,
        },
      );

      if (response.ok) {
        showCustomMessageModal(
          "Success",
          "Document updated successfully!",
          "success",
        );
        setShowEditModal(false);
        fetchDocuments(currentPage);
      } else {
        const errorData = await response.json();
        showCustomMessageModal(
          "Error",
          `Update failed: ${errorData.message || response.statusText}`,
          "error",
        );
      }
    } catch (error) {
      console.error("Network or unexpected error during update:", error);
      showCustomMessageModal(
        "Error",
        `An unexpected error occurred during update: ${error.message}`,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingDocument(null);
    setEditDocumentName("");
    setSelectedFiles([]);
  };

  const handleDeleteClick = (documentId) => {
    setDocumentToDeleteId(documentId);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDeleteId) return;

    setLoading(true);

    try {
      const authToken = getAuthToken();
      const headers = {};
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await fetch(
        `${API_URL}/realstate-website-document-delete/${documentToDeleteId}`,
        {
          method: "DELETE",
          headers: headers,
        },
      );

      if (response.ok) {
        showCustomMessageModal(
          "Success",
          "Document deleted successfully!",
          "success",
        );
        setShowDeleteConfirmModal(false);
        fetchDocuments(currentPage);
      } else {
        const errorData = await response.json();
        showCustomMessageModal(
          "Error",
          `Deletion failed: ${errorData.message || response.statusText}`,
          "error",
        );
      }
    } catch (error) {
      console.error("Network or unexpected error during delete:", error);
      showCustomMessageModal(
        "Error",
        `An unexpected error occurred during delete: ${error.message}`,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDeleteConfirmModal = () => {
    setShowDeleteConfirmModal(false);
    setDocumentToDeleteId(null);
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
          {/* 🎯 HEADER CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "24px",
                boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                flexShrink: 0,
              }}
            >
              📄
            </div>
            <div style={{ flex: 1, minWidth: "220px" }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "22px",
                  fontWeight: "800",
                  color: "#1e3a5f",
                  letterSpacing: "-0.5px",
                }}
              >
                Document List
              </h1>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Manage all your website documents
              </p>
            </div>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <Link
                to="/add-document"
                style={{
                  padding: "12px 18px",
                  borderRadius: "12px",
                  border: "none",
                  background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                  color: "#ffffff",
                  fontWeight: "700",
                  fontSize: "14px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 4px 12px rgba(30, 87, 153, 0.25)",
                  textDecoration: "none",
                }}
              >
                <FaPlus /> Add Document
              </Link>
            </div>
          </div>

          {/* 🎯 MAIN CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "5px",
                background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
              }}
            />

            <div style={{ padding: "24px" }}>
              {loading ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                    color: "#64748b",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      border: "3px solid #cbd5e1",
                      borderTop: "3px solid #2b6cb0",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  <p
                    style={{
                      marginTop: "16px",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Loading documents...
                  </p>
                </div>
              ) : documents && documents.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "#dc2626",
                    fontWeight: "700",
                  }}
                >
                  No documents found.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table bordered hover>
                    <thead
                      style={{
                        background:
                          "linear-gradient(135deg, #1e5799, #2b6cb0)",
                        color: "#ffffff",
                      }}
                    >
                      <tr>
                        <th>#</th>
                        <th>Document Name</th>
                        <th>Document URL</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents &&
                        documents.map((doc, index) => (
                          <tr key={doc._id}>
                            <td className="fw-bold">
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td>{doc.name}</td>
                            <td>
                              {doc.image ? (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "10px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {doc.image.split(",").map((img, index) => (
                                    <div
                                      key={index}
                                      style={{
                                        position: "relative",
                                        display: "inline-block",
                                      }}
                                    >
                                      {img.toLowerCase().endsWith(".pdf") ? (
                                        <div
                                          style={{
                                            width: "100px",
                                            height: "100px",
                                            background: "#f5f5f5",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            border: "1px solid #ddd",
                                            position: "relative",
                                          }}
                                        >
                                          <span>PDF</span>
                                        </div>
                                      ) : (
                                        <img
                                          src={`${imageAPIURL}/websiteDocumentUpload/${img.trim()}`}
                                          alt={`Uploaded ${index + 1}`}
                                          style={{
                                            width: "100px",
                                            height: "auto",
                                          }}
                                        />
                                      )}
                                      <a
                                        href={`${imageAPIURL}/websiteDocumentUpload/${img.trim()}`}
                                        download
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          position: "absolute",
                                          top: "5px",
                                          right: "5px",
                                          background: "rgba(0,0,0,0.6)",
                                          color: "white",
                                          borderRadius: "50%",
                                          width: "24px",
                                          height: "24px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          textDecoration: "none",
                                          cursor: "pointer",
                                        }}
                                        title="Download"
                                      >
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                          <polyline points="7 10 12 15 17 10"></polyline>
                                          <line
                                            x1="12"
                                            y1="15"
                                            x2="12"
                                            y2="3"
                                          ></line>
                                        </svg>
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                "No Document"
                              )}
                            </td>

                            <td>
                              <div className="dropdown">
                                <button
                                  className="btn light btn-action dropdown-toggle"
                                  type="button"
                                  id="dropdownMenuButton"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  style={{
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                  }}
                                >
                                  <BsThreeDots size={20} />
                                </button>

                                <ul
                                  className="dropdown-menu"
                                  aria-labelledby="dropdownMenuButton"
                                >
                                  <li className="dropdown-item">
                                    <button
                                      className="btn view_btn btn-sm me-1"
                                      onClick={() => handleViewClick(doc)}
                                      title="View Details"
                                      style={{
                                        borderRadius: "8px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      <FaEye /> View
                                    </button>
                                  </li>
                                  <li className="dropdown-item">
                                    <button
                                      className="btn edit_btn btn-sm me-1"
                                      onClick={() => handleEditClick(doc)}
                                      title="Edit"
                                      style={{
                                        borderRadius: "8px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      <FaEdit /> Edit
                                    </button>
                                  </li>

                                  <li className="dropdown-item">
                                    <button
                                      className="btn delete_btn btn-sm"
                                      onClick={() => handleDeleteClick(doc.id)}
                                      title="Delete"
                                      style={{
                                        borderRadius: "8px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      <RiDeleteBin3Fill /> Delete
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-end mt-4">
                  <Pagination className="d-flex">
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {[...Array(totalPages)].map((_, index) => (
                      <Pagination.Item
                        key={index + 1}
                        active={index + 1 === currentPage}
                        onClick={() => handlePageChange(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "12.5px",
              marginTop: "24px",
              fontWeight: "500",
            }}
          >
            © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All
            Rights Reserved.
          </p>
        </div>
      </div>

      {/* View Modal */}
      <Modal show={showViewModal} onHide={handleCloseViewModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Document Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewingDocument && (
            <>
              <p>
                <strong>Document Name:</strong> {viewingDocument.name}
              </p>
              <hr />
              <p>
                <strong>Files:</strong>
              </p>
              {viewingDocument.image && (
                <div
                  style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
                >
                  {viewingDocument.image.split(",").map((img, index) => (
                    <div key={index} style={{ textAlign: "center" }}>
                      {img.toLowerCase().endsWith(".pdf") ? (
                        <div
                          style={{
                            width: "100px",
                            height: "100px",
                            background: "#f5f5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid #ddd",
                          }}
                        >
                          <span style={{ fontSize: "1.2rem" }}>PDF</span>
                        </div>
                      ) : (
                        <img
                          src={`${imageAPIURL}/websiteDocumentUpload/${img.trim()}`}
                          alt={`Document ${index + 1}`}
                          style={{
                            maxWidth: "100px",
                            height: "auto",
                            border: "1px solid #ddd",
                          }}
                        />
                      )}
                      <a
                        href={`${imageAPIURL}/websiteDocumentUpload/${img.trim()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-link mt-1"
                      >
                        Open File
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleCloseViewModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Document</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateDocument}>
          <Modal.Body>
            <Form.Group controlId="editDocumentName" className="mb-3">
              <Form.Label
                style={{
                  fontSize: "12.5px",
                  fontWeight: "700",
                  color: "#1e3a5f",
                  marginBottom: "6px",
                }}
              >
                Document Name
              </Form.Label>
              <Form.Control
                type="text"
                value={editDocumentName}
                onChange={(e) => setEditDocumentName(e.target.value)}
                placeholder="Enter new document name"
                required
              />
            </Form.Group>

            <Form.Group controlId="editDocumentFiles" className="mb-3">
              <Form.Label
                style={{
                  fontSize: "12.5px",
                  fontWeight: "700",
                  color: "#1e3a5f",
                  marginBottom: "6px",
                }}
              >
                Existing Document Files
              </Form.Label>
              {editingDocument && editingDocument.image ? (
                <div
                  style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
                >
                  {editingDocument.image.split(",").map((img, index) => (
                    <div key={index} style={{ textAlign: "center" }}>
                      {img.toLowerCase().endsWith(".pdf") ? (
                        <div
                          style={{
                            width: "100px",
                            height: "100px",
                            background: "#f5f5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid #ddd",
                          }}
                        >
                          <span style={{ fontSize: "1.2rem" }}>PDF</span>
                        </div>
                      ) : (
                        <img
                          src={`${imageAPIURL}/websiteDocumentUpload/${img.trim()}`}
                          alt={`Document ${index + 1}`}
                          style={{
                            maxWidth: "100px",
                            height: "auto",
                            border: "1px solid #ddd",
                          }}
                        />
                      )}
                      <a
                        href={`${imageAPIURL}/websiteDocumentUpload/${img.trim()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-link mt-1"
                      >
                        Open File
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No existing files.</p>
              )}
            </Form.Group>

            <Form.Group controlId="newDocumentFiles">
              <Form.Label
                style={{
                  fontSize: "12.5px",
                  fontWeight: "700",
                  color: "#1e3a5f",
                  marginBottom: "6px",
                }}
              >
                Upload New Document Files (Optional)
              </Form.Label>
              <Form.Control type="file" multiple onChange={handleFileChange} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              style={{
                borderRadius: "10px",
                fontWeight: 700,
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                border: "none",
                boxShadow: "0 4px 12px rgba(30, 87, 153, 0.25)",
              }}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirmModal}
        onHide={handleCloseDeleteConfirmModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this document? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteConfirmModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom Message Modal */}
      <Modal show={showMessageModal} onHide={closeCustomMessageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title
            className={
              messageModalContent.type === "success"
                ? "text-success"
                : messageModalContent.type === "warning"
                  ? "text-warning"
                  : "text-danger"
            }
          >
            {messageModalContent.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{messageModalContent.text}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={closeCustomMessageModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

export default DocumentList;